import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, TrashIcon, EyeIcon, LockIcon, UnlockIcon, SearchIcon } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import TicketGroupDetail from './TicketGroupDetail';
import ConfirmModal from '../modals/ConfirmModal';
import EmptyState from '../common/EmptyState';
interface TicketsGroupTabProps {
  showFilters: boolean;
}
// Mock data for the table
const mockData = [{
  id: 'TG-2023-001',
  tourCode: 'HKGTOUR23',
  createdBy: 'John Smith',
  createdAt: '2023-06-15',
  refNo: 'REF-001-2023',
  ticketCount: 12,
  totalAmount: 15600.0,
  status: 'Open'
}, {
  id: 'TG-2023-002',
  tourCode: 'SINTOUR23',
  createdBy: 'Jane Doe',
  createdAt: '2023-06-16',
  refNo: 'REF-002-2023',
  ticketCount: 8,
  totalAmount: 9200.0,
  status: 'Locked'
}, {
  id: 'TG-2023-003',
  tourCode: 'TYOTOUR23',
  createdBy: 'Michael Johnson',
  createdAt: '2023-06-17',
  refNo: 'REF-003-2023',
  ticketCount: 15,
  totalAmount: 18750.0,
  status: 'Open'
}, {
  id: 'TG-2023-004',
  tourCode: 'BKKTOUR23',
  createdBy: 'Sarah Williams',
  createdAt: '2023-06-18',
  refNo: 'REF-004-2023',
  ticketCount: 6,
  totalAmount: 7200.0,
  status: 'Open'
}, {
  id: 'TG-2023-005',
  tourCode: 'MANTOUR23',
  createdBy: 'Robert Brown',
  createdAt: '2023-06-19',
  refNo: 'REF-005-2023',
  ticketCount: 10,
  totalAmount: 12000.0,
  status: 'Locked'
}];
const TicketsGroupTab: React.FC<TicketsGroupTabProps> = ({
  showFilters
}) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'lock' | 'unlock'>('lock');
  const [groupToToggle, setGroupToToggle] = useState<string | null>(null);
  const toggleSelectAll = () => {
    if (selectedGroups.length === mockData.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(mockData.map(group => group.id));
    }
  };
  const toggleSelectGroup = (groupId: string) => {
    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };
  const handleToggleLock = (groupId: string, currentStatus: string) => {
    setGroupToToggle(groupId);
    setConfirmAction(currentStatus === 'Locked' ? 'unlock' : 'lock');
    setIsConfirmModalOpen(true);
  };
  const confirmToggleLock = () => {
    if (!groupToToggle) return;
    console.log(`${confirmAction === 'lock' ? 'Locking' : 'Unlocking'} group ${groupToToggle}`);
    // Implement actual lock/unlock functionality here
    setIsConfirmModalOpen(false);
    setGroupToToggle(null);
  };
  // Filter groups based on search term
  const filteredGroups = mockData.filter(group => group.id.toLowerCase().includes(searchTerm.toLowerCase()) || group.tourCode.toLowerCase().includes(searchTerm.toLowerCase()) || group.refNo.toLowerCase().includes(searchTerm.toLowerCase()));
  if (selectedGroup) {
    const group = mockData.find(g => g.id === selectedGroup);
    if (group) {
      return <TicketGroupDetail group={group} onBack={() => setSelectedGroup(null)} data-id="element-1047" />;
    }
  }
  return <div className="space-y-6" data-id="element-1048">
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1049">
        <div className="p-4 border-b border-gray-200" data-id="element-1050">
          <div className="relative" data-id="element-1051">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1052">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1053" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by group ID, tour code, or reference number..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} aria-label="Search ticket groups" data-id="element-1054" />
          </div>
        </div>
        {filteredGroups.length > 0 ? <div data-id="element-1055">
            <div className="overflow-x-auto" data-id="element-1056">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Ticket groups" data-id="element-1057">
                <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-1058">
                  <tr data-id="element-1059">
                    <th scope="col" className="px-3 py-3" data-id="element-1060">
                      <div className="flex items-center" data-id="element-1061">
                        <input id="select-all" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={selectedGroups.length === mockData.length} onChange={toggleSelectAll} aria-label="Select all groups" data-id="element-1062" />
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1063">
                      Group ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1064">
                      Tour Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1065">
                      Created By
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1066">
                      Created At
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1067">
                      Ref No
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1068">
                      No. of Tickets
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1069">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1070">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1071">
                  {filteredGroups.map((group, index) => <tr key={group.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`} style={{
                transition: 'all 0.2s'
              }} data-id="element-1072">
                      <td className="px-3 py-4 whitespace-nowrap" data-id="element-1073">
                        <div className="flex items-center" data-id="element-1074">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={selectedGroups.includes(group.id)} onChange={() => toggleSelectGroup(group.id)} disabled={group.status === 'Locked'} aria-label={`Select group ${group.id}`} data-id="element-1075" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1076">
                        <button onClick={() => setSelectedGroup(group.id)} className="hover:text-blue-600 focus:outline-none focus:underline" data-id="element-1077">
                          {group.id}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1078">
                        {group.tourCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1079">
                        {group.createdBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1080">
                        {group.createdAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1081">
                        {group.refNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1082">
                        {group.ticketCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1083">
                        <div className="flex items-center" data-id="element-1084">
                          {group.status === 'Locked' ? <Tooltip content="Locked – no further edits allowed" data-id="element-1085">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1086">
                                <LockIcon className="h-3.5 w-3.5 mr-1" data-id="element-1087" />
                                Locked
                              </span>
                            </Tooltip> : <Tooltip content="Unlocked – editing allowed" data-id="element-1088">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1089">
                                <UnlockIcon className="h-3.5 w-3.5 mr-1" data-id="element-1090" />
                                Open
                              </span>
                            </Tooltip>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-1091">
                        {group.status !== 'Locked' && <>
                            <Tooltip content="Edit group" data-id="element-1092">
                              <Link to={`/edit-group/${group.id}`} className="text-blue-600 hover:text-blue-900" aria-label={`Edit group ${group.id}`} data-id="element-1093">
                                <EditIcon className="h-4 w-4 inline" data-id="element-1094" />
                              </Link>
                            </Tooltip>
                            <Tooltip content="Delete group" data-id="element-1095">
                              <button className="text-red-600 hover:text-red-900" aria-label={`Delete group ${group.id}`} data-id="element-1096">
                                <TrashIcon className="h-4 w-4 inline" data-id="element-1097" />
                              </button>
                            </Tooltip>
                          </>}
                        <Tooltip content="View group details" data-id="element-1098">
                          <button onClick={() => setSelectedGroup(group.id)} className="text-green-600 hover:text-green-900" aria-label={`View group ${group.id}`} data-id="element-1099">
                            <EyeIcon className="h-4 w-4 inline" data-id="element-1100" />
                          </button>
                        </Tooltip>
                        {group.status === 'Locked' ? <Tooltip content="Unlock group" data-id="element-1101">
                            <button className="text-blue-600 hover:text-blue-900" onClick={() => handleToggleLock(group.id, group.status)} aria-label={`Unlock group ${group.id}`} data-id="element-1102">
                              <UnlockIcon className="h-4 w-4 inline" data-id="element-1103" />
                            </button>
                          </Tooltip> : <Tooltip content="Lock group" data-id="element-1104">
                            <button className="text-gray-600 hover:text-gray-900" onClick={() => handleToggleLock(group.id, group.status)} aria-label={`Lock group ${group.id}`} data-id="element-1105">
                              <LockIcon className="h-4 w-4 inline" data-id="element-1106" />
                            </button>
                          </Tooltip>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-1107">
              <div className="flex-1 flex justify-between sm:hidden" data-id="element-1108">
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" aria-label="Previous page" data-id="element-1109">
                  Previous
                </button>
                <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" aria-label="Next page" data-id="element-1110">
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1111">
                <div data-id="element-1112">
                  <p className="text-sm text-gray-700" data-id="element-1113">
                    Showing <span className="font-medium" data-id="element-1114">1</span> to{' '}
                    <span className="font-medium" data-id="element-1115">{filteredGroups.length}</span>{' '}
                    of <span className="font-medium" data-id="element-1116">{mockData.length}</span>{' '}
                    results
                  </p>
                </div>
                <div data-id="element-1117">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1118">
                    <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" aria-label="Previous page" data-id="element-1119">
                      <span className="sr-only" data-id="element-1120">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" data-id="element-1121" />
                    </button>
                    <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" aria-label="Page 1" aria-current="page" data-id="element-1122">
                      1
                    </button>
                    <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" aria-label="Next page" data-id="element-1123">
                      <span className="sr-only" data-id="element-1124">Next</span>
                      <ChevronRightIcon className="h-5 w-5" data-id="element-1125" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div> : <EmptyState title="No ticket groups found" description="Try adjusting your search or create a new ticket group" actionLabel="Create Ticket Group" actionLink="/create-group" icon={<UsersIcon className="h-8 w-8 text-gray-400" data-id="element-1127" />} data-id="element-1126" />}
      </div>
      {isConfirmModalOpen && <ConfirmModal title={confirmAction === 'lock' ? 'Lock Ticket Group' : 'Unlock Ticket Group'} message={confirmAction === 'lock' ? 'Are you sure you want to lock this group? Locked groups cannot be edited.' : 'Are you sure you want to unlock this group? This will allow further edits.'} confirmLabel={confirmAction === 'lock' ? 'Lock Group' : 'Unlock Group'} confirmButtonClass={confirmAction === 'lock' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'} onConfirm={confirmToggleLock} onCancel={() => {
      setIsConfirmModalOpen(false);
      setGroupToToggle(null);
    }} data-id="element-1128" />}
    </div>;
};
export default TicketsGroupTab;